import { environment } from "@app/relay/relay-environment";
import clsx from "clsx";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { FunnelScreen, PrimaryButton } from "../screen";
import SourceScreen from "./source-screen";
import {
  CONNECTED,
  NO_DATA,
  STEP,
  STEPS,
  hubResolutions,
  stackPicks,
} from "../funnel";
import { LOGOS } from "../logos";

import type { dataHubStep_FinalizeMutation } from "@app/queries/dataHubStep_FinalizeMutation.graphql";
import type { HubProps } from "../types";

const finalizeMutation = graphql`
  mutation dataHubStep_FinalizeMutation($token: String) {
    leadFinalize(token: $token) {
      lead {
        status
        position
        waitDays
      }
      errors {
        message
      }
    }
  }
`;

// The two we hold OAuth apps for, which is what decides whether a resolved card
// reads "QuickBooks connected" or "Data uploaded".
const CONNECTABLE = new Set(["QUICKBOOKS", "STRIPE"]);

const NO_DATA_WARNING =
  "In order to get an accurate look at your business, you will need to " +
  "upload data for each of your account";

// The root of the data collection tree: one card per piece of software the lead
// named, each opening a screen of its own. A card is done when it has been
// connected, uploaded for, or explicitly written off — and the funnel does not
// leave this screen until they say they are finished, which is what "That's all
// the data I have" means and why saving here never advances the pointer.
export default function DataHubStep({
  step,
  lead,
  answers,
  busy,
  token,
  save,
  reload,
}: HubProps) {
  // Which card is open. Null is the grid.
  const [open, setOpen] = useState<string | null>(null);
  // Which card is one confirmation away from being written off.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const picks = stackPicks(answers).filter((id) => CATALOGUE[id]);
  const resolutions = hubResolutions(answers);

  const resolve = (id: string, resolution: string) => {
    save({
      fields: Object.entries({ ...resolutions, [id]: resolution }).map(
        ([key, value]) => ({ key, value })
      ),
    });
    setConfirming(null);
    setOpen(null);
  };

  const finish = () => {
    if (finalizing || busy) return;
    setFinalizing(true);
    commitMutation<dataHubStep_FinalizeMutation>(environment, {
      mutation: finalizeMutation,
      variables: { token },
      onCompleted: async (response) => {
        const message = response.leadFinalize?.errors?.[0]?.message;
        if (message) {
          setFinalizing(false);
          setError(message);
          return;
        }
        // The row comes back FINALIZED, and a finalized row is shown its spot
        // rather than the funnel — see funnel-view.
        await reload();
        setFinalizing(false);
      },
      onError: () => {
        setFinalizing(false);
        setError("Something went wrong. Please try again.");
      },
    });
  };

  if (open && CATALOGUE[open]) {
    return (
      <>
        <SourceScreen
          id={open}
          name={CATALOGUE[open]}
          lead={lead}
          token={token}
          busy={busy}
          onBack={() => setOpen(null)}
          onConnected={() => resolve(open, CONNECTED)}
          onNoData={() => setConfirming(open)}
          reload={reload}
        />
        {confirming && (
          <NoDataDialog
            onConfirm={() => resolve(confirming, NO_DATA)}
            onCancel={() => setConfirming(null)}
          />
        )}
      </>
    );
  }

  return (
    <FunnelScreen title={step.title} wide>
      <Score value={lead.completenessScore} />

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {picks.map((id) => (
          <SourceCard
            key={id}
            name={CATALOGUE[id]}
            logo={LOGOS[id]}
            resolution={resolutions[id]}
            connectedLabel={connectedLabel(id, CATALOGUE[id])}
            onClick={() => setOpen(id)}
          />
        ))}
      </div>

      {error && (
        <p className="mt-4 text-center text-caption font-normal text-red-600">
          {error}
        </p>
      )}

      <div className="mt-8">
        <PrimaryButton disabled={finalizing || busy} onClick={finish}>
          That&rsquo;s all the data I have
        </PrimaryButton>
      </div>
    </FunnelScreen>
  );
}

// How much of their picture we can see. Computed on the server
// (Onboarding::Scoring.completeness) rather than here, because the same number
// feeds the fit score that ranks the queue — two implementations of one curve
// would eventually disagree about someone's place in it.
//
// It moves on every source with data behind it and never reaches a hundred,
// because we can never know what we have not been told about.
function Score({ value }: { value: number }) {
  return (
    <div className="mt-5 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-caption text-zinc-700">Data completeness score</p>
        <p className="text-stat font-semibold tabular-nums text-zinc-900">
          {value}%
        </p>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-200">
        <div
          className="h-full rounded-full bg-blue-600 transition-[width] duration-500"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

function SourceCard({
  name,
  logo,
  resolution,
  connectedLabel,
  onClick,
}: {
  name: string;
  logo: string | undefined;
  resolution: string | undefined;
  connectedLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex flex-col rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
        resolution
          ? "border-zinc-200 bg-zinc-50 hover:border-zinc-400"
          : "border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50"
      )}
    >
      <span className="flex items-center gap-2.5">
        {logo ? (
          <img
            src={logo}
            alt=""
            className="block h-9 w-9 shrink-0 rounded-md"
          />
        ) : (
          <span
            aria-hidden
            className="block h-9 w-9 shrink-0 rounded-md bg-zinc-100"
          />
        )}
        <span className="text-body font-medium text-zinc-900">{name}</span>
      </span>

      <span className="mt-2.5 flex items-center gap-1.5 text-caption font-normal">
        {resolution === CONNECTED && (
          <>
            <Tick />
            <span className="text-emerald-700">{connectedLabel}</span>
          </>
        )}
        {resolution === NO_DATA && (
          <>
            <Cross />
            <span className="text-red-600">No data available</span>
          </>
        )}
        {!resolution && (
          <span className="text-zinc-500">Nothing yet — open to add it</span>
        )}
      </span>
    </button>
  );
}

// Writing a source off is the one decision on this screen that costs them
// something later, so it is the one that asks twice.
function NoDataDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="No data available"
        className="w-full max-w-[420px] rounded-2xl border border-zinc-200 bg-white p-6 shadow-lg"
      >
        <p className="text-body text-zinc-900">{NO_DATA_WARNING}</p>

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="h-11 flex-1 rounded-lg border border-zinc-300 bg-white text-body font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-11 flex-1 rounded-lg bg-blue-600 text-body font-semibold text-white transition-colors hover:bg-blue-700"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function Tick() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="h-4 w-4 text-emerald-600">
      <circle cx="8" cy="8" r="8" fill="currentColor" />
      <path
        d="M4.5 8.2l2.3 2.3 4.7-4.9"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Cross() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="h-4 w-4 text-red-600">
      <circle cx="8" cy="8" r="8" fill="currentColor" />
      <path
        d="M5.4 5.4l5.2 5.2M10.6 5.4l-5.2 5.2"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

// The names come from the stack question's own options, so this screen never
// carries a second copy of the list.
const CATALOGUE: Record<string, string> = Object.fromEntries(
  (STEPS.find((s) => s.key === STEP.STACK)?.options ?? []).map((o) => [
    o.id,
    o.name,
  ])
);

function connectedLabel(id: string, name: string) {
  return CONNECTABLE.has(id) ? `${name} connected` : "Data uploaded";
}
